import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { ToastrService } from 'ngx-toastr';
import { FormFieldComponent } from '@core/components/form-field/form-field.component';
import { SelectComponent } from '@core/components/select/select.component';
import { LoadingIconComponent } from '@core/components/loading-icon/loading-icon.component';
import { ModalService } from '@features/modals/services/modal.service';
import { PricesService } from '../../prices.service';
import { ReviewsService } from '../../../reviews/reviews.service';
import {
  CATEGORY_LABELS,
  PricingCategory,
  PricingItem,
  ScrapeResponseData,
  VALID_CATEGORIES,
} from '../../../shared/pricing.types';
import { SelectOptionConfig } from '@core/types/select-option-config.interface';
import { parsePrice } from '../../../shared/pricing.utils';
import { ScrapePanelComponent } from './scrape-panel/scrape-panel.component';

type EditPayload = Partial<{
  name: string;
  price: number;
  link: string;
  image: string;
  category: PricingCategory;
}>;

@Component({
  selector: 'app-price-edit-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormFieldComponent,
    SelectComponent,
    LoadingIconComponent,
    ReactiveFormsModule,
    ScrapePanelComponent,
  ],
  templateUrl: './price-edit-modal.component.html',
  styleUrl: './price-edit-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'modal-container flex flex-col' },
})
export class PriceEditModalComponent {
  private readonly toastr = inject(ToastrService);
  private readonly modalService = inject(ModalService);
  private readonly pricesService = inject(PricesService);
  private readonly reviewsService = inject(ReviewsService);

  readonly item = input<PricingItem | undefined>(undefined);
  readonly source = input<'prices' | 'reviews'>('prices');
  readonly isAdd = computed(() => !this.item());

  readonly name = new FormControl<string>('', [Validators.required]);
  readonly price = new FormControl<number | null>(null, [Validators.required]);
  readonly link = new FormControl<string>('');
  readonly image = new FormControl<string>('');
  readonly category = new FormControl<PricingCategory>('general', [Validators.required]);

  readonly itemForm = new FormGroup({
    name: this.name,
    price: this.price,
    link: this.link,
    image: this.image,
    category: this.category,
  });

  readonly categoryOptions: SelectOptionConfig[] = VALID_CATEGORIES.map((c) => ({
    label: CATEGORY_LABELS[c],
    key: c,
    fn: () => this.category.setValue(c),
  }));
  readonly selectedCategory = computed(() =>
    this.categoryOptions.find((o) => o.key === this.category.value) ?? this.categoryOptions[1]
  );

  readonly scrapeLoading = signal(false);
  readonly scrapeStillWorking = signal(false);
  readonly scrapeData = signal<ScrapeResponseData | null>(null);

  private readonly nameValue = toSignal(this.name.valueChanges, { initialValue: this.name.value });
  private readonly linkValue = toSignal(this.link.valueChanges, { initialValue: this.link.value });

  readonly canScrape = computed(() => {
    const n = (this.nameValue() ?? '').trim();
    const l = (this.linkValue() ?? '').trim();
    return n.length > 0 && l.length > 0;
  });

  constructor() {
    effect(() => {
      const item = this.item();
      if (!item) return;
      this.name.setValue(item.name);
      this.price.setValue(parsePrice(item.price));
      this.link.setValue(item.link ?? '');
      this.image.setValue(item.image ?? '');
      const normalized = (item.category ?? 'general').toLowerCase() as PricingCategory;
      if (VALID_CATEGORIES.includes(normalized)) this.category.setValue(normalized);
    });
  }

  async onScrape(): Promise<void> {
    if (!this.canScrape() || this.scrapeLoading()) return;
    this.scrapeLoading.set(true);
    const stillWorkingTimer = setTimeout(() => this.scrapeStillWorking.set(true), 30_000);
    try {
      const res = await this.pricesService.scrapeSkin(this.name.value!, this.link.value!);
      if (!res) return;
      this.scrapeData.set(res);
    } finally {
      clearTimeout(stillWorkingTimer);
      this.scrapeLoading.set(false);
      this.scrapeStillWorking.set(false);
    }
  }

  applySuggestedFromChild(usd: number): void {
    this.price.setValue(usd);
  }

  private buildEdits(original: PricingItem): EditPayload {
    const edits: EditPayload = {};
    if (this.name.value !== original.name) edits.name = this.name.value!;
    const origPrice = parsePrice(original.price);
    if (this.price.value !== origPrice) edits.price = this.price.value!;
    if ((this.link.value || '') !== (original.link || '')) edits.link = this.link.value || '';
    if ((this.image.value || '') !== (original.image || '')) edits.image = this.image.value || '';
    const origCat = (original.category ?? '').toLowerCase() as PricingCategory;
    if (this.category.value !== origCat) edits.category = this.category.value!;
    // When editing a Reviews-queue item, an empty diff would otherwise short-
    // circuit as "no changes" and never tell the BE the admin accepted the
    // suggested price — leaving the row stuck in the review queue. Force the
    // current price into the payload so the save always counts as an accept.
    if (this.source() === 'reviews' && edits.price === undefined) {
      edits.price = this.price.value!;
    }
    return edits;
  }

  async onSubmit(): Promise<void> {
    this.itemForm.markAllAsTouched();
    if (!this.itemForm.valid) {
      this.toastr.error('Form is not valid. Please check the inputs.');
      return;
    }

    if (this.isAdd()) {
      await this.pricesService.addSkin({
        name: this.name.value!,
        price: this.price.value!,
        image: this.image.value || undefined,
        link: this.link.value || undefined,
        category: this.category.value!,
      });
    } else {
      const edits = this.buildEdits(this.item()!);
      await this.pricesService.editSkin(this.item()!.id, edits);
      // BE promotes a reviewed item out of the review queue on edit, so the
      // saved row should disappear from the Reviews list. PricesService
      // already reloads prices internally; we also refresh reviews here so
      // the user sees the consistent state when they switch tabs.
      void this.reviewsService.loadItems();
    }
    this.modalService.close();
  }
}
